import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { getBankLogo } from '../data/logos';
import { formatDate } from '../utils/date';
import { API_BASE } from '../config';
import LanguageSwitcher from '../components/LanguageSwitcher';

interface OfficialEntity {
  id: number;
  code: string;
  name: string;
  category: string;
  country: string;
  officialUrls: string[];
  lastVerifiedAt?: string;
}

export function Directory() {
  const { t } = useTranslation();
  const [entities, setEntities] = useState<OfficialEntity[]>([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState('');
  const [search, setSearch] = useState('');

  const loadEntities = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (category) params.set('category', category);
      params.set('country', 'CZ');
      
      const res = await fetch(`${API_BASE}/v1/entities?${params}`);
      const data = await res.json();
      setEntities(data);
    } catch (error) {
      console.error('Failed to load entities:', error);
    } finally {
      setLoading(false);
    }
  }, [category]);

  useEffect(() => {
    loadEntities();
  }, [loadEntities]);

  const filteredEntities = search
    ? entities.filter((e) => e.name.toLowerCase().includes(search.toLowerCase()))
    : entities;

  return (
    <div className="page-container">
      <div className="content-wrapper--wide">
        <div className="mb-4 flex items-center justify-between">
          <Link to="/" className="btn-text inline-block">
            ← {t('directory.backToHome')}
          </Link>
          <LanguageSwitcher />
        </div>
        <h1 className="page-title">{t('directory.title')}</h1>

        <div className="card mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="form-label">
                {t('directory.search')}
              </label>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t('directory.searchPlaceholder')}
                className="input-field-compact"
              />
            </div>
            <div>
              <label className="form-label">
                {t('directory.category')}
              </label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="input-field-compact"
              >
                <option value="">{t('directory.allCategories')}</option>
                <option value="Bank">{t('directory.categoryBank')}</option>
                <option value="Delivery">{t('directory.categoryDelivery')}</option>
                <option value="E-commerce">{t('directory.categoryEcommerce')}</option>
              </select>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-8 text-gray-500">{t('common.loading')}</div>
        ) : (
          <div className="bg-white rounded-lg shadow-md divide-y">
            {filteredEntities.map((entity) => (
              <Link
                key={entity.id}
                to={`/entity/${entity.id}`}
                className="clickable-row block p-4"
                aria-label={`${t('directory.viewEntity', { name: entity.name })}`}
              >
                <div className="flex justify-between items-start gap-4">
                  <div className="shrink-0">
                    {entity.category === 'Bank' && getBankLogo(entity.code) ? (
                      <img
                        src={getBankLogo(entity.code)!.src}
                        alt={t('alt.logoOf', { name: entity.name })}
                        className="logo-thumb"
                        loading="lazy"
                        width={48}
                        height={48}
                      />
                    ) : (
                      <div className="logo-thumb logo-placeholder rounded">
                        <span className="sr-only">{t('alt.logoOf', { name: entity.name })}</span>
                      </div>
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="font-semibold text-gray-900">{entity.name}</h3>
                    <p className="text-sm text-gray-600 mt-1">
                      {entity.category} • {entity.country}
                    </p>
                    <div className="mt-2">
                      {entity.officialUrls.slice(0, 2).map((url, idx) => (
                        <div key={idx} className="text-sm text-blue-600">
                          {url}
                        </div>
                      ))}
                    </div>
                    {entity.lastVerifiedAt && (
                      <p className="text-xs text-gray-500 mt-2">
                        {t('directory.lastVerified')}: {formatDate(entity.lastVerifiedAt)}
                      </p>
                    )}
                  </div>
                  <span className="text-gray-400">→</span>
                </div>
              </Link>
            ))}
            {filteredEntities.length === 0 && (
              <div className="p-8 text-center text-gray-500">
                {t('directory.noResults')}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
